import {
  ChevronRight,
  DollarSign,
  HelpCircle,
  Menu,
  MessageCircle,
  Package,
  ShoppingCart,
  Star,
  Truck,
  Users,
} from 'lucide-react';
import { trackAssistantEvent } from '@/lib/assistantAnalytics';

/**
 * @param {string[]} keys
 * @param {object} mem sessionMemory snapshot (partySize, lastIntent, servicePreference, priceSensitivity)
 */
export function prioritizeFallbackKeysForMemory(keys, mem) {
  const pref = [];
  if (mem.lastIntent === 'order') pref.push('checkout_cart');
  if (mem.servicePreference === 'delivery') pref.push('order_delivery');
  if (mem.servicePreference === 'pickup' || mem.servicePreference === 'local') pref.push('order_pickup');
  if (mem.partySize >= 2) pref.push('duo_hint');
  if (mem.priceSensitivity === 'cheap') pref.push('cheap_hint');
  if (!pref.length) return keys;
  const seen = new Set();
  const out = [];
  for (const k of [...pref, ...(keys || [])]) {
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/**
 * @param {object} deps
 * @param {() => object} deps.getSessionMemory
 * @param {() => object|null} deps.getMenuData
 * @param {function} deps.assistantAnalyticsCtx
 * @param {function} deps.openBusinessWhatsApp
 * @param {function} deps.showMainMenu
 * @param {function} deps.handleMainChoiceFromText
 * @param {function} deps.handleOrderChoice
 * @param {function} deps.handleOrderChoiceFromText
 * @param {function} deps.getCartCount
 * @param {function} deps.touchCheckoutFromAssistant
 * @param {function} deps.openAssistantCheckout
 * @param {string} deps.assistantServicePrefKey
 */
export function createAssistantFallbackOptions(deps) {
  const {
    getSessionMemory,
    getMenuData,
    assistantAnalyticsCtx,
    openBusinessWhatsApp,
    showMainMenu,
    handleMainChoiceFromText,
    handleOrderChoice,
    handleOrderChoiceFromText,
    getCartCount,
    touchCheckoutFromAssistant,
    openAssistantCheckout,
    assistantServicePrefKey,
  } = deps;

  const prioritizeForMemory = (keys) => prioritizeFallbackKeysForMemory(keys, getSessionMemory());

  const optionsFromFreeTextFallback = (keys) => {
    const business = getMenuData()?.business || {};
    const out = [];
    const seen = new Set();
    for (const k of keys || []) {
      if (!k || seen.has(k)) continue;
      seen.add(k);
      if (k === 'catalog') {
        out.push({
          label: 'Explorar en el chat',
          value: 'ft-catalog',
          icon: Menu,
          action: () => handleMainChoiceFromText('order'),
        });
      } else if (k === 'whatsapp') {
        if (!business.whatsapp) continue;
        out.push({
          label: 'WhatsApp',
          value: 'ft-wa',
          icon: MessageCircle,
          action: () => {
            trackAssistantEvent('assistant_whatsapp_clicked', {
              ...assistantAnalyticsCtx(null),
              detected_intent: 'cta_whatsapp',
            });
            openBusinessWhatsApp();
          },
        });
      } else if (k === 'main') {
        out.push({
          label: 'Al inicio',
          value: 'ft-main',
          icon: ChevronRight,
          action: () => showMainMenu(),
        });
      } else if (k === 'order_hint') {
        out.push({
          label: 'Pedir',
          value: 'ft-order',
          icon: ShoppingCart,
          action: () => handleMainChoiceFromText('order'),
        });
      } else if (k === 'question_hint') {
        out.push({
          label: 'Una duda',
          value: 'ft-question',
          icon: HelpCircle,
          action: () => handleMainChoiceFromText('question'),
        });
      } else if (k === 'cheap_hint') {
        out.push({
          label: 'Ver lo más barato',
          value: 'ft-cheap',
          icon: DollarSign,
          action: () => handleOrderChoice('cheap'),
        });
      } else if (k === 'promo_hint') {
        out.push({
          label: 'Ver promos en el chat',
          value: 'ft-promo',
          icon: Menu,
          action: () => handleOrderChoice('special'),
        });
      } else if (k === 'popular_hint') {
        out.push({
          label: 'Lo más pedido',
          value: 'ft-popular',
          icon: Star,
          action: () => handleOrderChoiceFromText('popular'),
        });
      } else if (k === 'categories_hint') {
        out.push({
          label: 'Ver por categorías',
          value: 'ft-categories',
          icon: Menu,
          action: () => handleOrderChoiceFromText('categories'),
        });
      } else if (k === 'duo_hint') {
        out.push({
          label: 'Opciones para 2',
          value: 'ft-duo',
          icon: Users,
          action: () => handleOrderChoiceFromText('duo'),
        });
      } else if (k === 'checkout_cart') {
        out.push({
          label: 'Completa tus datos para terminar el pedido',
          value: 'ft-checkout',
          icon: MessageCircle,
          action: () => {
            trackAssistantEvent('assistant_checkout_clicked', {
              ...assistantAnalyticsCtx(null),
              detected_intent: 'cta_checkout',
              cart_count: getCartCount(),
            });
            touchCheckoutFromAssistant();
            if (getCartCount() > 0) openAssistantCheckout();
            else handleMainChoiceFromText('order');
          },
        });
      } else if (k === 'order_delivery') {
        if (!business.service_types?.includes('delivery')) continue;
        out.push({
          label: 'Pedir con delivery',
          value: 'ft-odel',
          icon: Truck,
          action: () => {
            sessionStorage.setItem('soluqr_checkout_service_pref', 'delivery');
            sessionStorage.setItem(assistantServicePrefKey, 'delivery');
            touchCheckoutFromAssistant();
            if (getCartCount() > 0) openAssistantCheckout();
            else handleMainChoiceFromText('order');
          },
        });
      } else if (k === 'order_pickup') {
        if (!business.service_types?.includes('retiro')) continue;
        out.push({
          label: 'Pedir para retiro',
          value: 'ft-opick',
          icon: Package,
          action: () => {
            sessionStorage.setItem('soluqr_checkout_service_pref', 'retiro');
            sessionStorage.setItem(assistantServicePrefKey, 'pickup');
            touchCheckoutFromAssistant();
            if (getCartCount() > 0) openAssistantCheckout();
            else handleMainChoiceFromText('order');
          },
        });
      }
    }
    return out;
  };

  return {
    prioritizeFallbackKeysForMemory: prioritizeForMemory,
    optionsFromFreeTextFallback,
  };
}
